import { MedicalType } from "@qendo/medical/types";
import { OptionType } from "@qendo/types";
import { Stack, useLocalSearchParams } from "expo-router";
import { useMemo } from "react";
import { View } from "react-native";
import { DeleteMedicalButton } from "~/components/form/health-profile/DeleteMedicalButton";
import { HeaderButton } from "~/components/HeaderButton";
import { DataList } from "~/components/layout/DataList";
import { DataListItem } from "~/components/layout/DataListItem";
import { BasicGallery } from "~/components/layout/gallery/BasicGallery";
import { Dosage } from "~/components/layout/health-profile/Dosage";
import { Page } from "~/components/layout/Page";
import { Subtitle, Title } from "~/components/layout/text";
import { Authorize } from "~/components/permission/Authorize";
import { Can } from "~/components/permission/Can";
import { Dismiss } from "~/components/permission/Dismiss";
import { Only } from "~/components/permission/Only";
import { Flex, Gap, Items, Padding } from "~/constants/layout";
import { useMedical } from "~/providers/MedicalProvider";
import { useOptions } from "~/providers/OptionsProvider";

const type = MedicalType.supplement;

export default function SupplementDetails() {
    const { id }: { id: string } = useLocalSearchParams();
    const { findMedical } = useMedical();
    const { findOption } = useOptions();
    const supplement = useMemo(() => findMedical(type, id), [id, findMedical]);

    if (!supplement) {
        return <Dismiss />;
    }

    const option = findOption(OptionType.supplements, supplement.option_id);

    return (
        <Authorize ability="view" section="supplements">
            <Stack.Screen
                options={{
                    title: "Supplement details",
                    headerRight: () => (
                        <Can ability="update" section="supplements">
                            <HeaderButton
                                title="Edit"
                                href={{ pathname: "/health-profile/supplements/[id]/edit", params: { id } }}
                            />
                        </Can>
                    ),
                }}
            />
            <Page spacer>
                <View style={[Padding.top_md, Gap.lg, Flex.flex]}>
                    <View style={[Items.start, Gap.xs]}>
                        <Only when={option}>
                            <Title size="xl">{option?.title}</Title>
                        </Only>
                        <Subtitle>{supplement.current ? "Current supplement" : "Past supplement"}</Subtitle>
                    </View>
                    <DataList>
                        <Dosage dosage={supplement.dosage} title="Supplement form" />
                        <DataListItem title="Description" data={supplement.description} />
                    </DataList>
                    <BasicGallery images={supplement.photograph_ids} />
                </View>
                <DeleteMedicalButton type={type} id={id} />
            </Page>
        </Authorize>
    );
}
